import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import Modal from '../../design-system/components/Modal/Modal';

export interface TestimonialData {
  name: string;
  role: string;
  quote: string;
  avatar?: string;
  rating?: number;
  /** Source de l'avis : Trustpilot, Google, App Store */
  source?: 'Trustpilot' | 'Google' | 'App Store';
  /** Lien vers la page des avis (ex. Trustpilot, fiche Google, App Store) */
  sourceUrl?: string;
}

interface TestimonialCardProps extends TestimonialData {
  className?: string;
  /** Limite le texte à N lignes dans la carte ; « Lire la suite » ouvre la citation complète (ex. marquee landing). */
  quoteMaxLines?: number;
  /** Layout resserré (carrousel mobile) — le desktop ne passe jamais ce flag. */
  compact?: boolean;
}

export default function TestimonialCard({
  name,
  role,
  quote,
  avatar,
  rating = 5,
  source,
  sourceUrl,
  className = '',
  quoteMaxLines,
  compact = false,
}: TestimonialCardProps) {
  const { t } = useTranslation('marketing');
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const [quoteOverflow, setQuoteOverflow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useLayoutEffect(() => {
    if (!quoteMaxLines || !quoteRef.current) {
      setQuoteOverflow(false);
      return;
    }
    const el = quoteRef.current;
    setQuoteOverflow(el.scrollHeight > el.clientHeight + 2);
  }, [quote, quoteMaxLines]);

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <motion.div
      className={`mk-testimonial${compact ? ' mk-testimonial--compact' : ''}${className ? ` ${className}` : ''}`}
      style={{
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: compact ? '24px' : '32px',
        padding: compact ? '20px 18px' : '40px',
        border: '1px solid rgba(165, 118, 92, 0.12)',
        boxShadow: compact ? '0 10px 28px rgba(0,0,0,0.05)' : '0 20px 50px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '12px' : '24px',
        width: '100%',
        height: compact ? '100%' : undefined,
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {!compact ? (
        <div style={{ position: 'absolute', top: '-10px', right: '-10px', opacity: 0.03, fontSize: '80px', pointerEvents: 'none' }}>✨</div>
      ) : null}

      {!compact ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 800, fontSize: '15px', color: '#1e293b' }}>
          {name}
        </span>
        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>·</span>
        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>{role}</span>
      </div>
      ) : null}

      <div style={{ display: 'flex', gap: '4px' }}>
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} size={14} fill="#A5765C" color="#A5765C" />
        ))}
      </div>

      <div style={{ position: 'relative' }}>
        <p
          ref={quoteRef}
          style={{
            fontFamily: 'Plus Jakarta Sans',
            fontSize: compact ? '15px' : '17px',
            lineHeight: compact ? 1.5 : 1.6,
            color: '#1e293b',
            margin: 0,
            fontWeight: 500,
            wordBreak: 'break-word',
            ...(quoteMaxLines
              ? ({
                  display: '-webkit-box',
                  WebkitLineClamp: quoteMaxLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                } as React.CSSProperties)
              : {}),
          }}
        >
          "{quote}"
        </p>
        {quoteMaxLines && quoteOverflow ? (
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '48px',
              background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.92) 55%, rgba(255,255,255,1))',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </div>

      {quoteMaxLines && quoteOverflow ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setModalOpen(true);
          }}
          className="mk-testimonial__read-more"
          style={{
            alignSelf: 'flex-start',
            marginTop: compact ? 0 : '-8px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontFamily: 'Plus Jakarta Sans',
            fontSize: '14px',
            fontWeight: 700,
            color: '#A5765C',
            padding: compact ? '8px 0' : 0,
            minHeight: compact ? 44 : undefined,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {t('testimonialCard.readMore')}
        </button>
      ) : null}

      {quoteMaxLines && (quoteOverflow || modalOpen) ? (
        <Modal
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          title={t('testimonialCard.modalTitle', { name })}
          width={520}
          zIndex={10050}
          footer={null}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: rating }).map((_, i) => (
                <Star key={i} size={16} fill="#A5765C" color="#A5765C" />
              ))}
            </div>
            <p
              style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '16px',
                lineHeight: 1.65,
                color: '#1e293b',
                margin: 0,
                fontWeight: 500,
              }}
            >
              "{quote}"
            </p>
            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 600 }}>
              {name} · {role}
            </div>
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#A5765C',
                  textDecoration: 'none',
                }}
              >
                {t('testimonialCard.viewOnSource', { source: source ?? 'Trustpilot' })}
              </a>
            ) : null}
          </div>
        </Modal>
      ) : null}

      <div style={{ display: 'flex', alignItems: compact ? 'flex-start' : 'center', gap: compact ? '12px' : '16px', borderTop: '1px solid rgba(165,118,92,0.08)', paddingTop: compact ? '14px' : '24px', marginTop: 'auto' }}>
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            style={{
              width: compact ? '44px' : '52px',
              height: compact ? '44px' : '52px',
              borderRadius: compact ? '14px' : '16px',
              objectFit: 'cover',
              border: '2px solid white',
              boxShadow: '0 8px 20px rgba(0,0,0,0.06)'
            }}
            loading="lazy"
          />
        ) : (
          <div
            style={{
              width: compact ? '44px' : '52px',
              height: compact ? '44px' : '52px',
              borderRadius: compact ? '14px' : '16px',
              background: 'linear-gradient(135deg, #A5765C 0%, #8b5e43 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: compact ? '14px' : '16px',
              fontWeight: 800,
              fontFamily: 'Plus Jakarta Sans',
              boxShadow: '0 8px 20px rgba(165,118,92,0.2)'
            }}
          >
            {initials}
          </div>
        )}
        <div>
          <div
            style={{
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 800,
              fontSize: compact ? '15px' : '16px',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {name}
            <div style={{ width: '14px', height: '14px', background: '#0ea5e9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: 'white' }}>✓</div>
          </div>
          <div
            style={{
              fontSize: compact ? '13px' : '14px',
              color: '#64748b',
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 500
            }}
          >
            {role}
          </div>
          {source && (
            <div style={{ marginTop: '8px' }}>
              {sourceUrl ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#A5765C',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {source} →
                </a>
              ) : (
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{source}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
